import { supabase } from "@/integrations/supabase/client";
import { normalizeOffices, type OfficeInput } from "@/lib/orgOffices";
import type { OrgEditorRow } from "@/lib/orgProfileEditorData";

/**
 * The one client writer for organization-profile edits — the wizard, the
 * profile editor, the website scan and the suggestion accept path all come
 * through here — and it is a thin wrapper over ONE database function,
 * `save_org_profile_patch` (migration 20260913000000).
 *
 * Why the merge moved server-side (eHomes, 2026-09-10): every client writer
 * used to read the document, merge in JavaScript and write the whole document
 * back. Two writers holding the same stale snapshot — the setup wizard and the
 * editor mounted under it, or two admins in two browsers — meant last write
 * wins and the other's work is gone with no error on screen. The function
 * merges over the row it has just locked, mirrors the indexed columns for the
 * keys the patch touched, replaces offices in the same transaction, and
 * refuses the write when the caller's `expectedUpdatedAt` is stale.
 *
 * A conflict is NOT an HTTP error. The result must be inspected; treating a
 * conflict as success is the exact failure this module exists to prevent,
 * which is why the return type is a discriminated union and not a boolean.
 *
 * It deliberately never writes `onboardingCompleted`. That flag used to mean
 * "stop asking"; profile strength decides now. An existing value is preserved
 * because orgOnboardingDecision reads it to avoid re-interrupting a brokerage
 * that completed the old wizard.
 */

/**
 * The wizard's patch keys. The editor sends any `ProfileData` key (see
 * orgProfileEditorPatch.ts); the function decides per key whether it is a
 * document key, a mirrored column, or column-only.
 */
export interface OrgProfilePatch {
  tagline?: string;
  description?: string;
  logo?: string;
  websiteUrl?: string;
  missionStatement?: string;
  whatMakesUsDifferent?: string;
  ourStory?: string;
  featuredQuote?: string;
  foundedYear?: string;
  /** org_type enum; column-only (never enters profile_data). */
  type?: string;
  serviceTags?: string[];
  marketsServed?: string[];
  selectedBenefits?: string[];
  compensationModels?: string[];
  trainingFrequency?: string[];
  /**
   * The three yes/no answers mirrored into columns. null is "not answered"
   * (OD-1): the server stores NULL, which the fit score does not compare.
   */
  providesLeads?: boolean | null;
  noRecurringFees?: boolean | null;
  partnershipOpportunities?: boolean | null;
  /** Tri-state; write it with leadGenerationPatch so the mirror stays true. */
  leadGenerationAnswer?: "provided" | "self" | "";
  // onboardingStep / onboardingDismissedAt are deliberately NOT here: they are
  // wizard bookkeeping, not profile content, and go through
  // saveOrgOnboardingBookmark (src/lib/orgOnboardingBookmark.ts), which stays
  // outside the compare-and-swap and the attribution stamp (#787).
}

/** Any profile key; the editor's patches are built from the whole form. */
export type OrgProfilePatchInput = OrgProfilePatch | Record<string, unknown>;

export interface SaveOrgProfileOptions {
  /**
   * The `organizations.updated_at` the caller last reconciled against, as the
   * raw string PostgREST returned — never round-tripped through `Date`, which
   * drops the microseconds the comparison depends on. `null`/absent means no
   * compare-and-swap (the wizard's narrow patches).
   */
  expectedUpdatedAt?: string | null;
  /**
   * Structured offices to replace in the same transaction. `undefined`/`null`
   * leaves offices alone; `[]` removes them all. Only pass this when the
   * locations actually changed — the replace churns every org_offices row.
   */
  offices?: OfficeInput[] | null;
}

/** The organizations row as `to_jsonb(row)` returns it. */
export interface SavedOrgRow extends OrgEditorRow {
  id: string;
  updated_at: string;
  profile_updated_by: string | null;
  is_published?: boolean | null;
  published_at?: string | null;
  [column: string]: unknown;
}

export type OrgProfileSaveResult =
  | { status: "saved"; updatedAt: string; row: SavedOrgRow }
  /** The expectation was stale. Nothing was written; `row` is the winner's. */
  | { status: "conflict"; updatedAt: string; row: SavedOrgRow }
  | { status: "failed"; message: string };

interface RpcResult {
  conflict?: unknown;
  updated_at?: unknown;
  organization?: unknown;
}

/** One RPC call. Never rejects; resolves `failed` on any error. */
const runSaveOrgProfilePatch = async (
  orgId: string,
  patch: OrgProfilePatchInput,
  opts: SaveOrgProfileOptions,
): Promise<OrgProfileSaveResult> => {
  try {
    const offices =
      opts.offices == null
        ? null
        : normalizeOffices(opts.offices).map((o) => ({ city: o.city, state: o.state }));

    // Not in the generated types yet (regeneration is deferred); the argument
    // and return shapes are pinned by the migration and this module's tests.
    const { data, error } = await supabase.rpc("save_org_profile_patch" as never, {
      p_org_id: orgId,
      p_patch: patch,
      p_expected_updated_at: opts.expectedUpdatedAt ?? null,
      p_offices: offices,
    } as never);

    if (error) {
      console.error("[orgProfilePatch] save_org_profile_patch failed", error);
      return { status: "failed", message: error.message };
    }

    const result = (data ?? null) as RpcResult | null;
    if (
      !result ||
      typeof result.conflict !== "boolean" ||
      typeof result.updated_at !== "string" ||
      !result.organization ||
      typeof result.organization !== "object" ||
      Array.isArray(result.organization)
    ) {
      console.error("[orgProfilePatch] unexpected response", data);
      return { status: "failed", message: "Unexpected response from save_org_profile_patch" };
    }

    const row = result.organization as SavedOrgRow;
    return result.conflict
      ? { status: "conflict", updatedAt: result.updated_at, row }
      : { status: "saved", updatedAt: result.updated_at, row };
  } catch (err) {
    console.error("[orgProfilePatch] save threw", err);
    return { status: "failed", message: err instanceof Error ? err.message : String(err) };
  }
};

/**
 * Per-org serialization. The database already serializes concurrent saves
 * (the function locks the row); what this chain adds is APPLICATION ORDER
 * from one browser: two unconditional wizard patches from one tab cannot land
 * B-then-A and let A overwrite B's overlapping keys, and results return to the
 * caller in call order so an editor's last-known `updated_at` never regresses.
 */
const saveChains = new Map<string, Promise<OrgProfileSaveResult>>();

export const saveOrgProfilePatch = (
  orgId: string,
  patch: OrgProfilePatchInput,
  opts: SaveOrgProfileOptions = {},
): Promise<OrgProfileSaveResult> => {
  const prior = saveChains.get(orgId) ?? Promise.resolve<OrgProfileSaveResult | null>(null);
  const run = prior.then(
    () => runSaveOrgProfilePatch(orgId, patch, opts),
    () => runSaveOrgProfilePatch(orgId, patch, opts),
  );
  saveChains.set(orgId, run);
  return run;
};
